import React, { useState } from "react";
import { Plus, Pencil, Trash2, Download, Receipt } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, downloadPdf } from "@/lib/api";
import { PrimaryButton, GhostButton, EmptyState, Field, inputCls, formatCOP } from "@/components/common";
import { DocTable, IconBtn, ClientSelect, LineItemsEditor, TotalsBox, itemsTotal, normalizeItems, ShareButtons } from "./shared";

const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({ client_id: "", client_name: "", client_nit: "", client_contact: "", issued_at: today(), items: [], note: "" });

export default function CuentasCobroTab({ cuentas, clients, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editing, setEditing] = useState(null);
  const clientMap = Object.fromEntries(clients.map(c => [c.client_id, c]));

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (cc) => { setEditing(cc.cc_id); setForm({ ...empty(), ...cc, note: cc.note ?? "" }); setOpen(true); };

  const pickClient = (client_id) => {
    const c = clients.find(x => x.client_id === client_id);
    setForm(f => ({ ...f, client_id, client_name: c?.name || "", client_nit: c?.nit || "", client_contact: c?.contact_name || "" }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Selecciona un cliente"); return; }
    if (form.items.length === 0) { toast.error("Agrega al menos un ítem"); return; }
    const payload = { ...form, items: normalizeItems(form.items), note: form.note.trim() ? form.note : null };
    try {
      if (editing) await api.put(`/cuentas-cobro/${editing}`, payload);
      else await api.post("/cuentas-cobro", payload);
      toast.success(editing ? "Cuenta de cobro actualizada" : "Cuenta de cobro creada");
      setOpen(false); reload();
    } catch { toast.error("No se pudo guardar"); }
  };

  const remove = async (cc) => {
    if (!window.confirm(`¿Eliminar la cuenta de cobro ${cc.number}?`)) return;
    await api.delete(`/cuentas-cobro/${cc.cc_id}`);
    toast.success("Eliminada"); reload();
  };

  const pdf = async (cc) => {
    try { await downloadPdf(`/cuentas-cobro/${cc.cc_id}/pdf`, `${cc.number}.pdf`); }
    catch { toast.error("No se pudo generar el PDF"); }
  };

  return (
    <div data-testid="cuentas-tab">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <p className="text-sm text-[#9CA3AF]">Cuentas de cobro con tus datos bancarios y nota tributaria. También puedes generarlas desde cada factura en Pagos.</p>
        <PrimaryButton data-testid="add-cc-btn" onClick={openNew}><Plus className="w-4 h-4"/> Nueva cuenta de cobro</PrimaryButton>
      </div>
      {cuentas.length === 0 ? (
        <EmptyState icon={Receipt} title="Aún no tienes cuentas de cobro" description="Créala manualmente o desde una factura en el módulo de Pagos con el botón de recibo."
          action={<PrimaryButton onClick={openNew}><Plus className="w-4 h-4"/> Crear cuenta de cobro</PrimaryButton>}/>
      ) : (
        <DocTable headers={["Número", "Cliente", "Fecha", "Origen", { label: "Total", right: true }, { label: "Acciones", right: true }]}>
          {cuentas.map(cc => (
            <tr key={cc.cc_id} data-testid={`cc-row-${cc.cc_id}`} className="border-b border-[#262A33] last:border-0 hover:bg-[#1A1D24] transition-colors">
              <td className="px-5 py-3 text-[#F5F5F7] font-mono text-xs">{cc.number}</td>
              <td className="px-5 py-3 text-[#F5F5F7]">{cc.client_name}{cc.client_nit && <span className="text-xs text-[#6B7280] ml-2">NIT {cc.client_nit}</span>}</td>
              <td className="px-5 py-3 text-[#9CA3AF]">{cc.issued_at}</td>
              <td className="px-5 py-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${cc.invoice_id ? "bg-[#F97316]/10 text-[#F97316] border-[#F97316]/20" : "bg-zinc-500/10 text-zinc-300 border-zinc-500/20"}`}>
                  {cc.invoice_id ? "Factura" : "Manual"}
                </span>
              </td>
              <td className="px-5 py-3 text-right text-[#F5F5F7] font-medium">{formatCOP(cc.total)}</td>
              <td className="px-5 py-3 text-right">
                <div className="inline-flex gap-1">
                  <IconBtn data-testid={`cc-pdf-${cc.cc_id}`} onClick={() => pdf(cc)} className="text-[#F97316] hover:bg-[#F97316]/10" title="Descargar PDF" aria-label="PDF"><Download className="w-4 h-4"/></IconBtn>
                  <ShareButtons path={`/cuentas-cobro/${cc.cc_id}`} clientEmail={clientMap[cc.client_id]?.email ?? ""} testId={`cc-${cc.cc_id}`}/>
                  <IconBtn data-testid={`cc-edit-${cc.cc_id}`} onClick={() => openEdit(cc)} className="text-[#9CA3AF] hover:text-[#F5F5F7] hover:bg-[#262A33]" aria-label="Editar"><Pencil className="w-4 h-4"/></IconBtn>
                  <IconBtn onClick={() => remove(cc)} className="text-[#6B7280] hover:text-red-400 hover:bg-red-500/10" aria-label="Eliminar"><Trash2 className="w-4 h-4"/></IconBtn>
                </div>
              </td>
            </tr>
          ))}
        </DocTable>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>{editing ? `Editar ${form.number || "cuenta de cobro"}` : "Nueva cuenta de cobro"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Cliente *"><ClientSelect clients={clients} value={form.client_id} onChange={pickClient} testId="cc-client-select"/></Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Razón social *"><input data-testid="cc-client-name" required className={inputCls} value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })}/></Field>
              <Field label="NIT"><input data-testid="cc-client-nit" className={inputCls} value={form.client_nit} onChange={e => setForm({ ...form, client_nit: e.target.value })}/></Field>
              <Field label="Fecha"><input type="date" className={inputCls} value={form.issued_at} onChange={e => setForm({ ...form, issued_at: e.target.value })}/></Field>
            </div>
            <LineItemsEditor items={form.items} onChange={items => setForm({ ...form, items })} withName={false} testPrefix="cc-item"/>
            <TotalsBox subtotal={itemsTotal(form.items)}/>
            <Field label="Nota tributaria" hint="Déjala vacía para usar la nota por defecto de Configuración.">
              <textarea rows={3} className={inputCls} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}/>
            </Field>
            <DialogFooter>
              <GhostButton type="button" onClick={() => setOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton data-testid="save-cc-btn" type="submit">{editing ? "Guardar" : "Crear cuenta de cobro"}</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
